import { makeAutoObservable, runInAction } from "mobx";
import { AxiosProgressEvent } from "axios";
import { getLocalAuthHeader, inventoryFileAPI } from "api-client";
import { buildingStore } from "store/Building/BuildingStore";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import type { UploadType } from ".";

class UploadFileStore {
  currentFile: File | undefined = undefined;
  uploadProgress: number = 0;
  isUploading: boolean = false;
  splitQuantityIntoObjects: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  setCurrentFile(file: File | undefined) {
    this.currentFile = file;
  }

  setSplitQuantityIntoObjects(value: boolean) {
    this.splitQuantityIntoObjects = value;
  }

  setUploadProgress(value: number) {
    this.uploadProgress = value;
  }

  setIsUploading(value: boolean) {
    this.isUploading = value;
  }

  reset() {
    this.currentFile = undefined;
    this.uploadProgress = 0;
    this.isUploading = false;
    this.splitQuantityIntoObjects = false;
  }

  async uploadFile(buildingId: string, uploadType: UploadType) {
    if (!this.currentFile) return;
    this.setIsUploading(true);
    try {
      const onUploadProgress = (event: AxiosProgressEvent) => {
        const total = event.total as number;
        runInAction(() => {
          this.uploadProgress = Math.round((100 * event.loaded) / total);
        });
      };
      if (uploadType === "IFC") {
        await this.uploadIfcFile(buildingId, onUploadProgress);
      } else if (uploadType === "CSV") {
        await this.uploadCsvFile(buildingId, onUploadProgress);
      }
      await buildingStore.getAllSourceFiles();
      const currentBuilding = await buildingStore.getCurrentBuildingDetails();
      if (currentBuilding) {
        buildingStore.setCurrentBuilding(currentBuilding);
      }
    } finally {
      this.setIsUploading(false);
    }
  }

  private async uploadCsvFile(
    buildingId: string,
    onUploadProgress: (event: AxiosProgressEvent) => void
  ) {
    if (!this.currentFile) return;
    const authHeader = getLocalAuthHeader();
    await inventoryFileAPI.inventoryApiV1RoutersInventoryFileUploadAndProcessInventoryFile(
      buildingId,
      this.currentFile,
      this.splitQuantityIntoObjects,
      {
        ...authHeader,
        onUploadProgress,
      }
    );
  }

  private async uploadIfcFile(
    buildingId: string,
    onUploadProgress: (event: AxiosProgressEvent) => void
  ) {
    if (!this.currentFile) return;
    const authHeader = getLocalAuthHeader();
    const response =
      await inventoryFileAPI.inventoryApiV1RoutersInventoryFileUploadAndProcessInventoryIfcCsvFile(
        buildingId,
        this.currentFile,
        {
          ...authHeader,
          onUploadProgress,
        }
      );
    MEStore.updateFileMappingStatus?.(response.data);
  }

  static instance: UploadFileStore;

  static getInstance(): UploadFileStore {
    if (!UploadFileStore.instance) {
      UploadFileStore.instance = new UploadFileStore();
    }
    return UploadFileStore.instance;
  }
}

export const uploadFileStore = UploadFileStore.getInstance();
